import type { TFunction } from 'i18next';
import {
  WaitingListWaitingListWriteModelRole,
  type WaitingListWaitingListWriteModel
} from '@r/api/generated/client.schemas.ts';
import type { WaitingListEditFormValues } from '@admin/components/company/waiting-list/waiting-list-edit-form-schema.ts';

export const waitingListEditCompanyStepFields: (keyof WaitingListEditFormValues)[] = [
  'companyName',
  'companyUniqueCode',
  'typology',
  'pointCoeff',
  'tva',
  'siren',
  'addressLine1',
  'addressLine2',
  'zipcode',
  'city',
  'countryAlpha3'
];

const acceptMandatoryFields: (keyof WaitingListEditFormValues)[] = [
  'companyName',
  'companyUniqueCode',
  'addressLine1',
  'zipcode',
  'city',
  'countryAlpha3',
  'email',
  'firstname',
  'lastname',
  'userUniqueCode',
  'role'
];

const acceptMandatoryFieldSet = new Set<keyof WaitingListEditFormValues>(acceptMandatoryFields);

export function isValidEmail(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function isAcceptMandatoryField(
  name: keyof WaitingListEditFormValues,
  typologyConversionMode: boolean
): boolean {
  if (name === 'typology') return typologyConversionMode;
  if (name === 'pointCoeff') return !typologyConversionMode;
  return acceptMandatoryFieldSet.has(name);
}

export function toNull(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function getAcceptMissingFields(
  values: WaitingListEditFormValues,
  typologyConversionMode: boolean
): (keyof WaitingListEditFormValues)[] {
  const missing = acceptMandatoryFields.filter((field) => {
    const value = values[field];
    if (field === 'email') return !isValidEmail(value as string | null | undefined);
    if (field === 'role') return !value;
    return !toNull(value as string | null | undefined);
  });
  if (typologyConversionMode && !toNull(values.typology)) {
    missing.push('typology');
  }
  if (!typologyConversionMode) {
    const pc = values.pointCoeff;
    if (
      pc === undefined ||
      pc === null ||
      (typeof pc === 'number' && (Number.isNaN(pc) || pc <= 0))
    ) {
      missing.push('pointCoeff');
    }
  }
  return missing;
}

export function acceptMissingFieldLabel(
  field: keyof WaitingListEditFormValues,
  t: TFunction
): string {
  switch (field) {
    case 'companyName':
      return t('label.company.name');
    case 'companyUniqueCode':
      return t('company.create.unique.code.label');
    case 'typology':
      return t('company.create.typology.name.label');
    case 'pointCoeff':
      return t('label.company.point.coefficient');
    case 'addressLine1':
      return t('label.company.address.line1');
    case 'zipcode':
      return t('label.company.zipcode');
    case 'city':
      return t('label.company.city');
    case 'countryAlpha3':
      return t('label.company.country');
    case 'email':
      return t('label.user.email');
    case 'firstname':
      return t('admin.waiting.list.edit.label.user.firstname');
    case 'lastname':
      return t('admin.waiting.list.edit.label.user.lastname');
    case 'userUniqueCode':
      return t('label.user.unique.code');
    case 'role':
      return t('label.user.role');
    default:
      return String(field);
  }
}

export function waitingListEditToPatch(
  values: WaitingListEditFormValues,
  dirty: Partial<Record<keyof WaitingListEditFormValues, boolean>>
): WaitingListWaitingListWriteModel {
  const payload: WaitingListWaitingListWriteModel = {};
  if (dirty.companyName) payload.companyName = toNull(values.companyName);
  if (dirty.companyUniqueCode) payload.companyUniqueCode = toNull(values.companyUniqueCode);
  if (dirty.typology) payload.typology = toNull(values.typology);
  if (dirty.tva) payload.tva = toNull(values.tva);
  if (dirty.siren) payload.siren = toNull(values.siren);
  if (dirty.addressLine1) payload.addressLine1 = toNull(values.addressLine1);
  if (dirty.addressLine2) payload.addressLine2 = toNull(values.addressLine2);
  if (dirty.zipcode) payload.zipcode = toNull(values.zipcode);
  if (dirty.city) payload.city = toNull(values.city);
  if (dirty.email) payload.email = toNull(values.email);
  if (dirty.firstname) payload.firstname = toNull(values.firstname);
  if (dirty.lastname) payload.lastname = toNull(values.lastname);
  if (dirty.userUniqueCode) payload.userUniqueCode = toNull(values.userUniqueCode);
  if (dirty.role) {
    payload.role =
      values.role === WaitingListWaitingListWriteModelRole.ROLE_MEMBER ||
      values.role === WaitingListWaitingListWriteModelRole.ROLE_MANAGER
        ? values.role
        : null;
  }
  if (dirty.countryAlpha3)
    payload.country = values.countryAlpha3 ? `/countries/${values.countryAlpha3}` : null;
  if (dirty.comment) payload.comment = toNull(values.comment);
  if (dirty.pointCoeff) {
    const pc = values.pointCoeff;
    payload.pointCoeff =
      pc !== undefined && pc !== null && typeof pc === 'number' && !Number.isNaN(pc) ? pc : null;
  }
  return payload;
}

export function isEmptyWaitingListPatch(payload: WaitingListWaitingListWriteModel): boolean {
  return Object.keys(payload).length === 0;
}
